import { ContentBody } from "@app/components/core/ContentBody/ContentBody"
import { ContentHeader } from "@app/components/core/ContentHeader/ContentHeader"
import Button from "@app/components/move2Library/Button/Button"
import Input from "@app/components/move2Library/Input/Input"
import { useCustomNavigation } from "@app/components/navigation/NavigationHandler"
import ImagePage from "@app/components/pageTypes/ImagePage/ImagePage"
import AnimatedRoute, { AnimationTypes } from "@app/components/routing/AnimatedRoute/AnimatedRoute"
import { setAccounts, setRelation, setTransponder } from "@app/store/actions/coreActions"
import { useRaiseError } from "@app/store/actions/errorActions"
import { useLogin } from "@app/store/service/authService"
import { useGetStrings } from "@app/store/service/cmsService"
import { useGetBankAccounts, useGetTransponder } from "@app/store/service/connectorService"
import { appStore } from "@app/store/store"
import tracking from "@app/utils/tracking"
import { emailPattern } from "@app/utils/validations"
import { useViewport } from "@tecreation/funnel-components"
import { ResponseError } from "@tecreation/js-state-react"
import { useCallback, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useSearchParams } from "react-router"
import "./LoginPage.css"

const LoginPage = () => {
	const [loadUserData, setloadUserData] = useState(false)

	const [URLSearchParams] = useSearchParams()
	const forwardTo = URLSearchParams.get("forwardTo")

	const { push } = useCustomNavigation()
	const { sm, md } = useViewport()
	const raiseError = useRaiseError()

	const [login, { isLoading }] = useLogin()
	const { data: transponder } = useGetTransponder(null, { skip: !loadUserData })
	const { data: account } = useGetBankAccounts(null, { skip: !loadUserData })
	const {
		data: {
			buttonSignIn,
			buttonForgotPassword,
			buttonCreateAccount,
			buttonForwardAsGuest,
			placeholderEMail,
			placeholderPassword,
			loginPageTitle,
			loginPageSubTitle,
			loginPageFormText,
			errorInputMissingEmail,
			errorInputMissingPassword,
			errorInputInvalidEmail,
		},
	} = useGetStrings(undefined, { defaultData: {} })

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		mode: "onSubmit",
		shouldFocusError: false,
		defaultValues: {
			email: "",
			password: "",
		},
	})

	useEffect(() => {
		if (transponder) {
			setTransponder(transponder)
		}
		if (account) {
			let thermencardAccount = account.find(e => e.isEmployee === false)
			let employeePoints = account.find(e => e.isEmployee === true && e.blocked == false)
			setAccounts(thermencardAccount, employeePoints)
		}
	}, [transponder, account])

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const goToForgotPassword = useCallback(e => {
		e.preventDefault()
		push("/forgot-password", AnimationTypes.slide)
	})
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const goToCreateAccount = useCallback(() => {
		push("/create-account", AnimationTypes.slide)
	})
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const goToAsGuest = useCallback(() => {
		setRelation({ guest: true })
		tracking.login("Gast")
		if (!forwardTo && !appStore.get("core.zoneId")) {
			push("/")
		} else {
			push(forwardTo ? forwardTo : "/order", AnimationTypes.slide)
		}
	})

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const goToOrderPage = useCallback(async () => {
		handleSubmit(async data => {
			try {
				let result = await login({
					email: data.email,
					password: data.password,
				}).unwrap()

				setRelation(result)
				setloadUserData(true)

				if (!forwardTo && !appStore.get("core.zoneId")) {
					push("/")
				} else {
					push(forwardTo ? forwardTo : "/order", AnimationTypes.slide)
				}
			} catch (e) {
				if (e instanceof ResponseError) {
					if (e.response.data.errorMessage === "wrn.Relation.Unverified") {
						push("/account-not-verified", undefined, { email: data.email, relationToken: e.response.data.relationToken })
					}
				}
				raiseError(e)
			}
		})()
	})

	return (
		<AnimatedRoute className="h-100 loginPage">
			<ImagePage allowUnknownTypes>
				<ContentHeader colorStyle="white" canGoBack animation={AnimationTypes.slide}>
					{loginPageTitle}
					<br /> <span className="f4 f-first">{loginPageSubTitle}</span>
				</ContentHeader>
				<ContentBody className="flex-1 flex flex-column" scrollable>
					<ActionDiv URLSearchParams={URLSearchParams} />
					<div className="flex-1 flex-center flex-column flex-lg-row color-fg-primary px-6">
						<div className="flex-lg-1 w-100" style={{ height: !sm && !md ? "250px" : "auto" }}>
							<form
								aria-labelledby="login-form-title"
								onSubmit={e => {
									e.preventDefault()
									goToOrderPage()
									return false
								}}
							>
								<span className="regular f4" id="login-form-title">
									{loginPageFormText}
								</span>
								<Input
									name="email"
									type="email"
									placeholder={placeholderEMail}
									className="mt-1 primary"
									formRegister={register("email", {
										required: {
											value: true,
											message: errorInputMissingEmail,
										},
										pattern: {
											value: emailPattern,
											message: errorInputInvalidEmail,
										},
									})}
									autocomplete="username"
									errors={errors}
									hideLabel
								/>
								<Input
									name="password"
									type="password"
									placeholder={placeholderPassword}
									className="mt-3 primary"
									formRegister={register("password", {
										required: {
											value: true,
											message: errorInputMissingPassword,
										},
									})}
									autocomplete="current-password"
									errors={errors}
									hideLabel
								/>

								<a className="color-fg-secondary f5 mt-1 block" href="" onClick={goToForgotPassword}>
									{buttonForgotPassword}
								</a>
								<div>
									<Button style="primary" className="mt-4" fillWidth={sm || md} isLoading={isLoading} onClick={goToOrderPage}>
										{buttonSignIn}
									</Button>
								</div>
							</form>
						</div>

						<div
							style={{ width: sm || md ? "100%" : 0, height: sm || md ? "0" : "80%" }}
							className="border-left border-bottom color-border-primary mx-4 my-8"
						/>
						<div className="flex-lg-1 w-100 pt-lg-4" style={{ height: !sm && !md ? "250px" : "auto" }}>
							<div className="flex mb-3 flex-1">
								<Button onClick={goToCreateAccount} style="secondary" fillWidth>
									{buttonCreateAccount}
								</Button>
							</div>
							<div className="flex mt-3 flex-1">
								<Button style="primary" onClick={goToAsGuest} fillWidth>
									{buttonForwardAsGuest}
								</Button>
							</div>
						</div>
					</div>
				</ContentBody>
			</ImagePage>
		</AnimatedRoute>
	)
}

const ActionDiv = ({ URLSearchParams }) => {
	const { data: stringData } = useGetStrings()
	const { loginPageAccountCreatedText, loginPageForgotPasswordText, loginPageNewPasswordSetText, loginPageVerifiedAccountText, loginPageResendedMail } =
		stringData ? stringData : {}

	if (URLSearchParams.get("action") === "resendMail") {
		return (
			<div className="color-fg-white text-center color-bg-success p-4 rounded-3 mb-4">
				<span className="f4">{loginPageResendedMail}</span>
			</div>
		)
	}
	if (URLSearchParams.get("action") === "createdAccount") {
		return (
			<div className="color-fg-white text-center color-bg-success p-4 rounded-3 mb-4">
				<span className="f4">{loginPageAccountCreatedText}</span>
			</div>
		)
	}
	if (URLSearchParams.get("action") === "forgotPassword") {
		return (
			<div className="color-fg-white text-center color-bg-attention p-4 rounded-3 mb-4">
				<span className="f4">{loginPageForgotPasswordText}</span>
			</div>
		)
	}
	if (URLSearchParams.get("action") === "savedPassword") {
		return (
			<div className="color-fg-white text-center color-bg-success p-4 rounded-3 mb-4">
				<span className="f4">{loginPageNewPasswordSetText}</span>
			</div>
		)
	}

	if (URLSearchParams.get("action") === "verifiedAccount") {
		return (
			<div className="color-fg-white text-center color-bg-success p-4 rounded-3 mb-4">
				<span className="f4">{loginPageVerifiedAccountText}</span>
			</div>
		)
	}
}

export default LoginPage
